<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-hidden h-full w-full flex items-center justify-center z-50"
  >
    <div
      v-if="loading"
      class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50"
    >
      <div class="spinner"></div>
    </div>
    <div
      class="bg-white rounded-lg shadow-xl w-full max-w-9xl mx-4 my-6 flex flex-col"
      style="max-height: 90vh"
    >
      <!-- Fixed Header -->
      <div class="p-6 border-b border-gray-200">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-800">
            Survey Preview: {{ survey.title }}
          </h2>
          <button
            @click="$emit('close')"
            class="text-gray-600 hover:text-gray-800 focus:outline-none"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
        <div class="mt-4 grid grid-cols-2 gap-4 text-sm">
          <p><strong>Survey Type:</strong> {{ survey.type }}</p>
          <p><strong>Status:</strong> {{ survey.status }}</p>
          <p><strong>Start Date:</strong> {{ formatDate(survey.startDate) }}</p>
          <p><strong>End Date:</strong> {{ formatDate(survey.endDate) }}</p>
        </div>
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <!-- Student ID Input -->
        <div class="mb-6">
          <label for="studentId" class="block text-sm font-medium text-gray-700"
            >Enter Student ID</label
          >
          <div class="mt-1 flex rounded-md shadow-sm">
            <input
              type="text"
              name="studentId"
              id="studentId"
              v-model="studentId"
              class="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300"
              placeholder="Student ID"
            />
            <button
              @click="fetchStudentData"
              class="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm"
            >
              Fetch Data
            </button>
          </div>
          <p v-if="studentDataError" class="mt-2 text-sm text-red-600">
            {{ studentDataError }}
          </p>
        </div>

        <!-- Survey Content -->
        <div v-if="studentData" class="space-y-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              Student Information
            </h3>
            <p class="mt-1">
              <strong>Name:</strong> {{ studentData.name }}
              <button
                @click="sendTestEmail(studentData)"
                class="bg-primary text-white p-2 rounded-lg hover:bg-primary transition duration-300 text-center font-medium"
              >
                Send Test Email
              </button>
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium text-gray-900 mb-4">
              Survey Questions
            </h3>
            <!-- University-level survey -->
            <div
              v-if="survey.type == 'University'"
              class="grid grid-cols-1 gap-6"
            >
              <div
                v-for="module in moduleStaff"
                :key="module.moduleCode"
                class="bg-white shadow-md rounded-lg p-4"
                v-if="isModuleActive(module.moduleCode)"
              >
                <div
                  class="bg-primary text-white p-1 flex justify-between items-center"
                  @click.stop="hide(module)"
                >
                  <h3 class="text-lg font-bold">
                    {{ module.moduleCode }} - {{ module.moduleName }}
                  </h3>
                  <button
                    class="text-gray-600 hover:text-gray-800 focus:outline-none"
                  >
                    <svg
                      class="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      v-if="
                        !hideQuestions.find((x) => x.id == module.moduleCode)
                          .visible
                      "
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 4v16m8-8H4"
                      ></path>
                    </svg>
                    <svg
                      class="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      v-if="
                        hideQuestions.find((x) => x.id == module.moduleCode)
                          .visible
                      "
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M20 12H4"
                      ></path>
                    </svg>
                  </button>
                </div>
                <div class="relative" v-if="module.needSelect">
                  <label class="block text-sm font-medium text-gray-700"
                    >Select Staff Members</label
                  >
                  <div
                    v-if="module.staff.length > 1"
                    class="mt-1 mb-2 space-y-2"
                  >
                    <button
                      v-if="getSelectedStaff(module, 0).length == 0"
                      @click="openTeacherSelection(module, -2)"
                      class="ml-2 text-sm bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark transition-colors duration-200"
                    >
                      Select Teachers
                    </button>
                    <button
                      v-if="getSelectedStaff(module, 0).length > 0"
                      @click="openTeacherSelection(module, -2)"
                      class="ml-2 text-sm bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark transition-colors duration-200"
                    >
                      Change Teachers
                    </button>
                  </div>
                  <div v-else class="mt-1 text-sm text-gray-700">
                    <!--    {{ module.staff[0].name }} -->
                  </div>
                </div>
                <div
                  v-for="(question, questionIndex) in survey.questions"
                  :key="questionIndex"
                  v-if="getSelectedStaff(module, 0).length > 0"
                >
                  <!--<p class="font-medium mb-2">{{ question.text }}</p> -->
                  <div
                    class="bg-gray-300 text-black p-1 flex justify-between items-center"
                  >
                    {{ questionIndex + 1 }}. {{ question.text }}
                  </div>
                  <div class="mb-2">
                    <div
                      v-for="staffId in getSelectedStaff(module, questionIndex)"
                      :key="staffId"
                    >
                      <div
                        v-if="question.type === 'rating'"
                        class="flex items-center space-x-1 mb-2 mt-2"
                      >
                        <!--   <h5 class="font-medium text-sm mb-2">
                            Answer for {{ getStaffName(module, staffId) }}
                          </h5>-->
                        <span class="font-medium text-sm" style="width: 300px"
                          >For
                          <span
                            @click="showStaffImage(module, staffId)"
                            style="text-decoration: underline; cursor: pointer"
                            >{{ getStaffName(module, staffId) }}</span
                          ></span
                        >

                        <template v-for="n in question.maxRating || 5">
                          <button
                            :key="n"
                            @click="
                              setRating(
                                module.moduleCode,
                                questionIndex,
                                n,
                                staffId
                              )
                            "
                            class="w-6 h-6 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-full"
                            :aria-label="`Rate ${n} out of ${
                              question.maxRating || 5
                            }`"
                          >
                            <svg
                              class="w-6 h-6"
                              :class="[
                                n <=
                                getRating(
                                  module.moduleCode,
                                  questionIndex,
                                  staffId
                                )
                                  ? 'text-yellow-400'
                                  : 'text-gray-300',
                              ]"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                              />
                            </svg>
                          </button>
                        </template>
                        <span class="ml-2 text-sm text-gray-600">
                          ({{
                            getRating(
                              module.moduleCode,
                              questionIndex,
                              staffId
                            ) || 0
                          }}/{{ question.maxRating || 5 }})
                        </span>
                      </div>
                      <div v-else-if="question.type === 'single'" class="mt-1">
                        <select
                          v-model="
                            getAnswer(module.moduleCode, questionIndex, staffId)
                              .value
                          "
                          class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        >
                          <option value="">Select an option</option>
                          <option
                            v-for="option in question.options"
                            :key="option"
                            :value="option"
                          >
                            {{ option }}
                          </option>
                        </select>
                      </div>
                      <div
                        v-else-if="question.type === 'multiple'"
                        class="space-y-2"
                      >
                        <div
                          v-for="option in question.options"
                          :key="option"
                          class="flex items-center"
                        >
                          <input
                            type="checkbox"
                            :id="`${module.moduleCode}-${questionIndex}-${staffId}-${option}`"
                            :value="option"
                            v-model="
                              getAnswer(
                                module.moduleCode,
                                questionIndex,
                                staffId
                              ).value
                            "
                            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label
                            :for="`${module.moduleCode}-${questionIndex}-${staffId}-${option}`"
                            class="ml-2 block text-sm text-gray-900"
                          >
                            {{ option }}
                          </label>
                        </div>
                      </div>
                      <div v-else-if="question.type === 'text'" class="mt-1">
                        <input
                          type="text"
                          v-model="
                            getAnswer(module.moduleCode, questionIndex, staffId)
                              .value
                          "
                          class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mt-4" v-if="getSelectedStaff(module, 0).length > 0">
                  <label
                    class="bg-primary text-white p-1 flex justify-between items-center mb-2"
                    >Optional Comments</label
                  >
                  <div
                    v-for="staffId in getSelectedStaff(module, 0)"
                    :key="staffId"
                  >
                    <h5 class="font-medium text-sm mb-2">
                      For {{ getStaffName(module, staffId) }}
                    </h5>
                    <textarea
                      v-model="moduleComments[module.moduleCode][staffId]"
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      rows="3"
                      placeholder="Add your comments for this module..."
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
            <!-- School/Department-level survey -->
            <div v-else class="grid grid-cols-1 gap-6">
              <div
                v-for="module in moduleStaff"
                :key="module.moduleCode"
                class="bg-white shadow-md rounded-lg p-4"
              >
                <div
                  class="flex justify-between items-center"
                  @click.stop="hide(module)"
                >
                  <h3 class="text-lg font-bold mb-2">
                    {{ module.moduleCode }} - {{ module.moduleName }}
                  </h3>
                  <button
                    class="text-gray-600 hover:text-gray-800 focus:outline-none"
                  >
                    <svg
                      class="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      v-if="
                        !hideQuestions.find((x) => x.id == module.moduleCode)
                          .visible
                      "
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 4v16m8-8H4"
                      ></path>
                    </svg>
                    <svg
                      class="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      v-if="
                        hideQuestions.find((x) => x.id == module.moduleCode)
                          .visible
                      "
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M20 12H4"
                      ></path>
                    </svg>
                  </button>
                </div>
                <div
                  v-for="(question, questionIndex) in survey.questions"
                  :key="questionIndex"
                  class="mb-4"
                  v-if="
                    hideQuestions.find((x) => x.id == module.moduleCode).visible
                  "
                >
                  <!--<p class="font-medium mb-2">{{ question.text }}</p> -->
                  <div
                    class="bg-primary text-white p-1 flex justify-between items-center"
                  >
                    {{ questionIndex + 1 }}. {{ question.text }}
                  </div>
                  <div v-if="question.option === 'module/staff'" class="mb-2">
                    <div class="relative">
                      <label
                        :for="`staff-select-${module.moduleCode}-${questionIndex}`"
                        class="block text-sm font-medium text-gray-700"
                        >Select Staff Members</label
                      >
                      <div
                        v-if="module.staff.length > 1"
                        class="mt-1 space-y-2"
                      >
                        <button
                          @click="openTeacherSelection(module, questionIndex)"
                          class="ml-2 text-sm bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark transition-colors duration-200"
                        >
                          Select Teachers
                        </button>
                      </div>
                      <div v-else class="mt-1 text-sm text-gray-700">
                        {{ module.staff[0] ? module.staff[0].name : "" }}
                      </div>
                    </div>
                    <div
                      v-for="staffId in getSelectedStaff(module, questionIndex)"
                      :key="staffId"
                      class="mt-4"
                    >
                      <h5 class="font-medium text-sm mb-2">
                        For {{ getStaffName(module, staffId) }}
                      </h5>
                      <div
                        v-if="question.type === 'rating'"
                        class="flex items-center space-x-1"
                      >
                        <template v-for="n in question.maxRating || 5">
                          <button
                            :key="n"
                            @click="
                              setRating(
                                module.moduleCode,
                                questionIndex,
                                n,
                                staffId
                              )
                            "
                            class="w-6 h-6 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-full"
                            :aria-label="`Rate ${n} out of ${
                              question.maxRating || 5
                            }`"
                          >
                            <svg
                              class="w-6 h-6"
                              :class="[
                                n <=
                                getRating(
                                  module.moduleCode,
                                  questionIndex,
                                  staffId
                                )
                                  ? 'text-yellow-400'
                                  : 'text-gray-300',
                              ]"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                              />
                            </svg>
                          </button>
                        </template>
                        <span class="ml-2 text-sm text-gray-600">
                          ({{
                            getRating(
                              module.moduleCode,
                              questionIndex,
                              staffId
                            ) || 0
                          }}/{{ question.maxRating || 5 }})
                        </span>
                      </div>
                      <div v-else-if="question.type === 'single'" class="mt-1">
                        <select
                          v-model="
                            getAnswer(module.moduleCode, questionIndex, staffId)
                              .value
                          "
                          class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        >
                          <option value="">Select an option</option>
                          <option
                            v-for="option in question.options"
                            :key="option"
                            :value="option"
                          >
                            {{ option }}
                          </option>
                        </select>
                      </div>
                      <div
                        v-else-if="question.type === 'multiple'"
                        class="space-y-2"
                      >
                        <div
                          v-for="option in question.options"
                          :key="option"
                          class="flex items-center"
                        >
                          <input
                            type="checkbox"
                            :id="`${module.moduleCode}-${questionIndex}-${staffId}-${option}`"
                            :value="option"
                            v-model="
                              getAnswer(
                                module.moduleCode,
                                questionIndex,
                                staffId
                              ).value
                            "
                            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label
                            :for="`${module.moduleCode}-${questionIndex}-${staffId}-${option}`"
                            class="ml-2 block text-sm text-gray-900"
                          >
                            {{ option }}
                          </label>
                        </div>
                      </div>
                      <div v-else-if="question.type === 'text'" class="mt-1">
                        <input
                          type="text"
                          v-model="
                            getAnswer(module.moduleCode, questionIndex, staffId)
                              .value
                          "
                          class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <!-- Non-module/staff questions -->
                    <div
                      v-if="question.type === 'rating'"
                      class="flex items-center space-x-1"
                    >
                      <template v-for="n in question.maxRating || 5">
                        <button
                          :key="n"
                          @click="
                            setRating(module.moduleCode, questionIndex, n)
                          "
                          class="w-6 h-6 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-full"
                          :aria-label="`Rate ${n} out of ${
                            question.maxRating || 5
                          }`"
                        >
                          <svg
                            class="w-6 h-6"
                            :class="[
                              n <= getRating(module.moduleCode, questionIndex)
                                ? 'text-yellow-400'
                                : 'text-gray-300',
                            ]"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                            />
                          </svg>
                        </button>
                      </template>
                      <span class="ml-2 text-sm text-gray-600">
                        ({{
                          getRating(module.moduleCode, questionIndex) || 0
                        }}/{{ question.maxRating || 5 }})
                      </span>
                    </div>
                    <div v-else-if="question.type === 'single'" class="mt-1">
                      <select
                        v-model="
                          getAnswer(module.moduleCode, questionIndex).value
                        "
                        class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      >
                        <option value="">Select an option</option>
                        <option
                          v-for="option in question.options"
                          :key="option"
                          :value="option"
                        >
                          {{ option }}
                        </option>
                      </select>
                    </div>
                    <div
                      v-else-if="question.type === 'multiple'"
                      class="space-y-2"
                    >
                      <div
                        v-for="option in question.options"
                        :key="option"
                        class="flex items-center"
                      >
                        <input
                          type="checkbox"
                          :id="`${module.moduleCode}-${questionIndex}-${option}`"
                          :value="option"
                          v-model="
                            getAnswer(module.moduleCode, questionIndex).value
                          "
                          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          :for="`${module.moduleCode}-${questionIndex}-${option}`"
                          class="ml-2 block text-sm text-gray-900"
                        >
                          {{ option }}
                        </label>
                      </div>
                    </div>
                    <div v-else-if="question.type === 'text'" class="mt-1">
                      <input
                        type="text"
                        v-model="
                          getAnswer(module.moduleCode, questionIndex).value
                        "
                        class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else-if="moduleStaff.length === 0"
          class="text-center py-4 text-gray-500"
        >
          No data available. Please enter a valid Student ID and fetch the data.
        </div>
      </div>
    </div>
    <TeacherSelectionModal
      v-if="showTeacherModal"
      :show="showTeacherModal"
      :module-code="currentModule?.moduleCode"
      :initial-selected-teachers="
        selectedTeachers[currentModule?.moduleCode] || []
      "
      :teachers="currentModule?.staff || []"
      @close="closeTeacherSelection"
      @select="handleTeacherSelection"
    />

    <!-- modal for staff image-->
    <div
      v-if="currentImage"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-hidden h-full w-full flex items-center justify-center z-50"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 my-6 flex flex-col"
      >
        <div
          class="p-6 border-b border-gray-200 flex justify-between items-center"
        >
          <h2 class="text-2xl font-bold text-gray-800">Staff Image</h2>
          <button
            @click="currentImage = ''"
            class="text-gray-600 hover:text-gray-800 focus:outline-none"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
        <div class="p-6 flex justify-center">
          <img
            :src="currentImage"
            alt="Staff Image"
            class="max-w-full max-h-full rounded-lg"
            style="max-height: 800px"
          />
        </div>
      </div>
    </div>

    <div
      v-if="tooltipContent"
      :style="tooltipStyle"
      class="absolute bg-black text-white p-2 rounded-md text-sm z-50"
    >
      {{ tooltipContent }}
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { surveyApi } from "@/api/surveyManagement";
import TeacherSelectionModal from "./TeacherSelectionModal.vue";

export default {
  name: "SurveyPreviewModal",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    survey: {
      type: Object,
      required: true,
    },
  },
  components: {
    TeacherSelectionModal,
  },
  data() {
    return {
      studentId: "",
      studentData: null,
      studentDataError: null,
      moduleStaff: [],
      moduleQuestions: {},
      moduleComments: {},
      showStaffColumn: true,
      selectedTeachers: {},
      studentId: "",
      moduleDates: {},
      tooltipContent: null,
      tooltipStyle: {
        display: "none",
        position: "fixed",
      },
      currentQuestionIndex: -1,
      selectedTeachers: {},
      showTeacherModal: false,
      currentModule: null,
      hideQuestions: [],
      currentImage: "",
      loading: false,
    };
  },
  computed: {
    ...mapState({
      userRole: (state) => state.auth.userRole,
    }),
  },
  methods: {
    async sendTestEmail(studentData) {
      console.log(studentData);
      console.log(this.survey.id);
      this.loading = true;
      await surveyApi.sendSingleEmail(this.survey.id, studentData.studentId);
      this.loading = false;
      alert("Send Successfully");
    },
    hide(module) {
      let m = this.hideQuestions.find((x) => x.id == module.moduleCode);
      m.visible = !m.visible;
    },
    async processStudentData() {
      if (!this.studentData) return;
      this.selectedTeachers = {};
      this.moduleStaff = this.studentData.modules.map((module) => {
        const staffSet = new Set();
        let needSelect = false;

        module.activities.forEach((activity) => {
          if (activity.staff.length > 1) needSelect = true;
          activity.staff.forEach((staff) => {
            if (staff) staffSet.add(staff);
            //add default staff for non staff act
          });
        });
        this.$set(this.moduleQuestions, module.moduleCode, {});
        this.$set(this.moduleComments, module.moduleCode, {}); // Initialize as object for teacher-specific comments

        const staffIds = Array.from(staffSet).map((staff) => staff.staffId);

        console.log(staffIds);
        this.$set(
          this.moduleQuestions[module.moduleCode],
          "default",
          this.survey.questions.map((q) => ({
            value: q.type === "multiple" ? [] : null,
            selectedStaff: staffIds.length === 1 ? staffIds : [],
          }))
        );

        const staffArray = Array.from(staffSet).filter(
          (staff, index, self) =>
            index === self.findIndex((s) => s.staffId === staff.staffId)
        );

        /*
        this.$set(
          this.selectedTeachers,
          module.moduleCode,
          staffArray.length === 1 ? staffArray : []
        );
        */

        this.$set(
          this.selectedTeachers,
          module.moduleCode,
          !needSelect ? staffArray : []
        );

        if (this.survey.type == "University" && !needSelect) {
          for (let i = 0; i < this.survey.questions.length; i++) {
            this.getAnswer(module.moduleCode, i).selectedStaff = staffArray.map(
              (t) => t.staffId
            );
          }
        }

        return {
          moduleCode: module.moduleCode,
          moduleName: module.name,
          staff: staffArray,
          needSelect: needSelect,
        };
      });
    },
    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleString();
    },
    async fetchStudentData() {
      this.studentDataError = null;
      this.studentData = null;

      if (!this.studentId) {
        this.studentDataError = "Please enter a student ID";
        return;
      }

      try {
        this.loading = true
        let data = await surveyApi.getStudentDataForSurvey(
          this.survey.id,
          this.studentId
        );
        this.moduleDates = await surveyApi.getModuleDates(this.survey.id);
        this.studentData = data;
        data.modules.forEach((module) => {
          this.hideQuestions.push({ id: module.moduleCode, visible: true });
        });
        this.processStudentData();
        this.loading = false
      } catch (error) {
        console.error("Error fetching student data:", error);
        this.studentDataError =
          "Failed to fetch student data. Please try again.";
      }
    },
    showTooltip(event, content) {
      this.tooltipContent = content;
      this.tooltipStyle = {
        display: "block",
        position: "fixed",
        top: `${event.clientY + 10}px`,
        left: `${event.clientX + 10}px`,
      };
    },
    hideTooltip() {
      this.tooltipContent = null;
      this.tooltipStyle.display = "none";
    },
    openTeacherSelection(module, questionIndex = -1) {
      this.currentModule = module;
      this.currentQuestionIndex = questionIndex;
      this.showTeacherModal = true;
    },
    closeTeacherSelection() {
      this.showTeacherModal = false;
      this.currentModule = null;
    },
    handleTeacherSelection(teachers) {
      if (this.currentModule) {
        if (this.currentQuestionIndex == -1) {
          this.selectedTeachers[this.currentModule.moduleCode] = teachers;
          this.$set(this.moduleQuestions, this.currentModule.moduleCode, {});
          teachers.forEach((teacher) => {
            this.$set(
              this.moduleQuestions[this.currentModule.moduleCode],
              teacher.staffId,
              this.survey.questions.map((q) => ({
                value: q.type === "multiple" ? [] : null,
                selectedStaff: null,
              }))
            );
            this.$set(this.moduleComments, this.currentModule.moduleCode, {
              ...this.moduleComments[this.currentModule.moduleCode],
              [teacher.staffId]: "",
            });
          });
        } else if (this.currentQuestionIndex == -2) {
          this.selectedTeachers[this.currentModule.moduleCode] = teachers;
          for (let i = 0; i < this.survey.questions.length; i++) {
            this.getAnswer(this.currentModule.moduleCode, i).selectedStaff =
              teachers.map((t) => t.staffId);
          }
          this.currentQuestionIndex = -1;
        } else {
          this.getAnswer(
            this.currentModule.moduleCode,
            this.currentQuestionIndex
          ).selectedStaff = teachers.map((t) => t.staffId);
          this.currentQuestionIndex = -1;
        }

        this.$forceUpdate();
      }
      this.closeTeacherSelection();
    },
    isModuleActive(moduleCode) {
      if (this.survey.type !== "University") return true;
      let _moduleDates = this.moduleDates.filter(
        (x) => x.moduleCode == moduleCode
      );
      console.log(_moduleDates);
      const moduleDates = _moduleDates[_moduleDates.length - 1];
      if (!moduleDates) return false; // If no dates are set, assume it's active
      const now = new Date();
      console.log(moduleCode + " " + moduleDates.startDate);
      return (
        now >= new Date(moduleDates.startDate) &&
        now <= new Date(moduleDates.endDate)
      );
    },
    setRating(moduleCode, questionIndex, rating, staffId = "default") {
      if (!this.moduleQuestions[moduleCode]) {
        this.$set(this.moduleQuestions, moduleCode, {});
      }
      if (!this.moduleQuestions[moduleCode][staffId]) {
        this.$set(
          this.moduleQuestions[moduleCode],
          staffId,
          this.survey.questions.map(() => ({ value: null, selectedStaff: [] }))
        );
      }
      this.$set(
        this.moduleQuestions[moduleCode][staffId][questionIndex],
        "value",
        rating
      );
      this.$forceUpdate();
    },
    getRating(moduleCode, questionIndex, staffId = "default") {
      return (
        this.moduleQuestions[moduleCode]?.[staffId]?.[questionIndex]?.value || 0
      );
    },
    getAnswer(moduleCode, questionIndex, staffId = "default") {
      if (!this.moduleQuestions[moduleCode]) {
        this.$set(this.moduleQuestions, moduleCode, {});
      }
      if (!this.moduleQuestions[moduleCode][staffId]) {
        this.$set(
          this.moduleQuestions[moduleCode],
          staffId,
          this.survey.questions.map((q) => ({
            value: q.type === "multiple" ? [] : null,
            selectedStaff: [],
          }))
        );
      }
      const answer = this.moduleQuestions[moduleCode][staffId][questionIndex];
      if (
        this.survey.questions[questionIndex].type === "multiple" &&
        !Array.isArray(answer.value)
      ) {
        answer.value = [];
      }
      return answer;
    },
    getStaffName(module, staffId) {
      const staff = module.staff.find((s) => s.staffId === staffId);
      return staff ? staff.name : "Unknown Staff";
    },
    showStaffImage(module, staffId) {
      const staff = module.staff.find((s) => s.staffId === staffId);
      this.currentImage = staff ? staff.image : "Unknown Staff";
    },
    getSelectedStaff(module, questionIndex) {
      const selectedStaff = this.getAnswer(
        module.moduleCode,
        questionIndex
      ).selectedStaff;
      return selectedStaff.length > 0
        ? selectedStaff
        : module.staff.length === 1
        ? [module.staff[0] ? module.staff[0].staffId : ""]
        : [];
    },
  },
};
</script>

<style scoped>
/* Custom scrollbar styles */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #666;
}
</style>
